import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth.jsx";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { LayoutDashboard, Truck, Users as UsersIcon, Settings as SettingsIcon, LogOut, Menu, X, Shield, BookOpen, Sparkles } from "lucide-react";
import { useState } from "react";

const NAV = [
    { to: "/dashboard", label: "Reklamationer", icon: LayoutDashboard, roles: ["admin", "kundeservice", "medarbejder"] },
    { to: "/handleplan", label: "Handleplan", icon: BookOpen, roles: ["admin", "kundeservice", "medarbejder"] },
    { to: "/ai-instrukser", label: "AI-instrukser", icon: Sparkles, roles: ["admin", "kundeservice"] },
    { to: "/leverandorer", label: "Leverandører", icon: Truck, roles: ["admin", "kundeservice"] },
    { to: "/ledere", label: "Ledere", icon: Shield, roles: ["admin", "kundeservice"] },
    { to: "/brugere", label: "Brugere", icon: UsersIcon, roles: ["admin"] },
    { to: "/indstillinger", label: "Indstillinger", icon: SettingsIcon, roles: ["admin"] },
];

const ROLE_LABELS = { admin: "Administrator", kundeservice: "Kundeservice", medarbejder: "Medarbejder" };

export default function DashboardLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const handleLogout = () => {
        logout();
        navigate("/login");
    };

    const allowed = NAV.filter((n) => n.roles.includes(user?.role));

    return (
        <div className="min-h-screen bg-[#FAFAF9]">
            {/* Mobile topbar */}
            <div className="lg:hidden sticky top-0 z-30 bg-white border-b border-[#E5E1DA] px-4 py-3 flex items-center justify-between">
                <Logo to="/dashboard" size="sm" subtitle={null} />
                <Button variant="ghost" size="sm" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </Button>
            </div>

            <div className="flex">
                {/* Sidebar */}
                <aside
                    className={`${
                        open ? "block" : "hidden"
                    } lg:block fixed lg:sticky top-0 left-0 h-screen w-64 bg-[#FAFAF9] border-r border-[#E5E1DA] z-40 overflow-y-auto`}
                >
                    <div className="p-6 hidden lg:block">
                        <Logo to="/dashboard" size="md" />
                    </div>

                    <nav className="px-3 py-2 space-y-1">
                        {allowed.map((item) => {
                            const Icon = item.icon;
                            return (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    onClick={() => setOpen(false)}
                                    data-testid={`nav-${item.to.replace("/", "")}`}
                                    className={({ isActive }) =>
                                        `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                            isActive
                                                ? "bg-[#F0EBE1] text-[#2C2A29]"
                                                : "text-[#5C5854] hover:bg-[#F4F2EF]"
                                        }`
                                    }
                                >
                                    <Icon className="w-4 h-4" />
                                    {item.label}
                                </NavLink>
                            );
                        })}
                    </nav>

                    <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-[#E5E1DA] bg-white">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="w-9 h-9 rounded-full bg-[#D2C9B9] text-[#2C2A29] flex items-center justify-center text-sm font-semibold">
                                {user?.name?.[0]?.toUpperCase() || "?"}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-[#2C2A29] truncate" data-testid="user-name">
                                    {user?.name}
                                </div>
                                <div className="text-xs text-[#78736E]">{ROLE_LABELS[user?.role]}</div>
                            </div>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleLogout}
                            className="w-full border-[#E5E1DA] text-[#5C5854] hover:bg-[#F0EBE1]"
                            data-testid="logout-button"
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Log ud
                        </Button>
                    </div>
                </aside>

                {/* Main */}
                <main className="flex-1 min-h-screen lg:pl-0">
                    <div className="max-w-7xl mx-auto px-6 py-8 lg:py-12">
                        <Outlet />
                    </div>
                </main>
            </div>
        </div>
    );
}
